import {
  tokenStorage,
  refreshTokenStorage,
} from '@/modules/infrastructure/users/services/auth.api';
import type {
  CurrentUserContext,
  SwitchUserContextData,
  UserContextItem,
} from '../types/userContext.types';
import {
  getAvailableUserContexts,
  getCurrentUserContext,
  logoutUserContext,
  switchUserContext,
} from './userContext.api';

/**
 * Сохранить новые токены из ответа переключения/сброса контекста.
 * Права позиции отдельно не храним — они приезжают в payload нового токена.
 */
export function applyContextTokens(data?: SwitchUserContextData): boolean {
  if (!data) return false;
  let applied = false;
  if (data.access_token) {
    tokenStorage.set(data.access_token);
    applied = true;
  }
  if (data.refresh_token) {
    refreshTokenStorage.set(data.refresh_token);
  }
  return applied;
}

/**
 * Полная перезагрузка приложения, чтобы getInitialState заново получил
 * профиль, права и меню под новую позицию.
 */
export function reloadForNewContext(): void {
  window.location.reload();
}

/** Переключиться на выбранную позицию: получить новые токены и перезагрузить. */
export async function switchContextAndReload(orgStructureId: string): Promise<void> {
  const response = await switchUserContext({ org_structure_id: orgStructureId });
  applyContextTokens(response?.data);
  reloadForNewContext();
}

/** Бэкенд иногда отдаёт одну позицию дважды с одним org_structure_id. */
export function uniqueByOrgStructure(items: UserContextItem[]): UserContextItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const id = item.org_structure_id;
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/**
 * В основном профиле бэкенд всё равно называет какую-то орг. структуру, поэтому
 * по org_structure_id позицию не опознать. Признак работы от позиции —
 * подменённый пользователь: current_user_id перестаёт совпадать с parent_user_id.
 */
export function isPositionContext(current?: CurrentUserContext | null): boolean {
  return Boolean(current && current.current_user_id !== current.parent_user_id);
}

export interface UserContextState {
  current: CurrentUserContext | null;
  contexts: UserContextItem[];
}

let pendingContextState: Promise<UserContextState> | null = null;

/**
 * Текущая позиция и список доступных нужны и шапке, и экрану выбора позиции.
 * Запрашиваем их один раз на загрузку страницы и отдаём обоим.
 */
export function loadUserContexts(): Promise<UserContextState> {
  if (!pendingContextState) {
    pendingContextState = (async () => {
      const [currentRes, contextsRes] = await Promise.all([
        getCurrentUserContext().catch(() => undefined),
        getAvailableUserContexts().catch(() => undefined),
      ]);
      return {
        current: currentRes?.data ?? null,
        contexts: uniqueByOrgStructure(
          Array.isArray(contextsRes?.data) ? contextsRes.data : [],
        ),
      };
    })();
  }
  return pendingContextState;
}

/** Вернуться в основной родительский профиль. */
export async function resetContextAndReload(): Promise<void> {
  const response = await logoutUserContext();
  applyContextTokens(response?.data);
  reloadForNewContext();
}
